import React from 'react';
import type { PayRunResult } from '../types';

interface SuccessStateProps {
  result: PayRunResult;
  onStartNew: () => void;
  onNext: () => void;
}

function fmt(n: number): string {
  return n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return s;
  }
}

export default function SuccessState({ result, onStartNew, onNext }: SuccessStateProps) {
  const hasErrors = result.results.some((r) => r.status === 'error');
  const hasWarnings = result.results.some((r) => r.status === 'warning');
  const contractorResults = result.results.filter((r) => r.status === 'success');
  const expectedSuperTotal = result.expectedSuperTotal ?? result.totalSuper;

  return (
    <div className="flex flex-col items-center py-12 px-8 text-center max-w-lg mx-auto">
      {/* Icon */}
      <div className={[
        'flex h-16 w-16 items-center justify-center rounded-full mb-5',
        hasErrors ? 'bg-amber-100' : 'bg-emerald-100',
      ].join(' ')}>
        {hasErrors ? (
          <svg className="h-8 w-8 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="h-8 w-8 text-emerald-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {/* Heading */}
      <h2 className="text-2xl font-bold text-gray-900 mb-1">
        {hasErrors ? 'Pay Run Created with Warnings' : `Draft Pay Run ${result.payRunReused ? 'Reused' : 'Created'}`}
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {hasErrors
          ? 'Some payslips could not be updated. Review the details below.'
          : 'Super amounts have been applied to mapped contractor payslips. Open in Xero to review before posting.'}
      </p>

      {/* Stats */}
      <div className="w-full rounded-xl border border-gray-200 bg-white divide-y divide-gray-100 mb-6 text-left">
        <Row label="Status" value={result.payRunReused ? 'Draft Pay Run reused' : 'Draft Pay Run created'} />
        <Row label="PayRunID" value={result.xeroPayRunId} />
        <Row label="Pay period" value={`${fmtDate(result.payPeriodStartDate)} – ${fmtDate(result.payPeriodEndDate)}`} />
        <Row label="Mapped contractors updated" value={String(contractorResults.length)} />
        <Row label="Final expected super total" value={`$${fmt(expectedSuperTotal)}`} bold />
      </div>

      {contractorResults.length > 0 && (
        <div className="w-full mb-6 rounded-xl border border-gray-200 bg-white text-left">
          {contractorResults.map((r, i) => (
            <div key={`${r.employeeId}-${i}`} className="flex justify-between px-4 py-3 text-sm border-b border-gray-100 last:border-b-0">
              <span className="font-medium text-gray-900">{r.contractorName}</span>
              <span className="font-semibold text-gray-900">${fmt(r.superAmount)} super written</span>
            </div>
          ))}
        </div>
      )}

      {/* Per-contractor results */}
      {(hasErrors || hasWarnings) && (
        <div className="w-full mb-6 space-y-2">
          {result.results.filter((r) => r.status !== 'success').map((r, i) => (
            <div
              key={i}
              className={[
                'rounded-lg border px-4 py-3 text-sm text-left',
                r.status === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700',
              ].join(' ')}
            >
              <span className="font-medium">{r.contractorName}</span>
              {r.error && <span className="ml-1">— {r.error}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Primary CTA */}
      <a
        href={result.xeroUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary w-full mb-3 text-base py-3"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
        </svg>
        Open Xero Pay Runs
      </a>

      <button onClick={onNext} className="btn-primary w-full mb-3 text-base py-3">
        Next: Remaining Contractors
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      <button onClick={onStartNew} className="btn-secondary w-full text-sm">
        Start New Import
      </button>

      <p className="mt-4 text-xs text-gray-400">
        Remember: you must manually click <strong>Post Pay Run</strong> in Xero to finalise.
      </p>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between items-center px-4 py-3 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={bold ? 'font-bold text-brand-700' : 'font-medium text-gray-900'}>{value}</span>
    </div>
  );
}
